import { useEffect, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import {
  USER_ROLE_LABELS,
  USER_ROLES,
  roleCanHoldMachineToken,
  type ApiTokenCreateResponse,
  type ApiTokenListResponse,
  type ApiTokenResponse,
  type PublicApiToken,
  type PublicUser,
  type UserListResponse,
  type UserResponse,
  type UserRole,
} from '@inventohub/shared';
import { ApiClientError, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';

interface Draft {
  email: string;
  name: string;
  role: UserRole;
}

const EMPTY: Draft = { email: '', name: '', role: 'staff' };

export function UsersPage(): JSX.Element {
  const { user } = useAuth();
  const [users, setUsers] = useState<PublicUser[]>([]);
  const [tokensByUser, setTokensByUser] = useState<Record<string, PublicApiToken[]>>({});
  const [rawByUser, setRawByUser] = useState<Record<string, string>>({});
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tokenBusy, setTokenBusy] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const body = await apiRequest<UserListResponse>('/api/users');
        if (cancelled) return;
        setUsers(body.users);
        const tokens = await Promise.all(
          body.users.map(async (entry) => {
            try {
              const listed = await apiRequest<ApiTokenListResponse>(
                `/api/users/${entry.id}/tokens`,
              );
              return [entry.id, listed.tokens] as const;
            } catch {
              return [entry.id, []] as const;
            }
          }),
        );
        if (!cancelled) setTokensByUser(Object.fromEntries(tokens));
      } catch (caught: unknown) {
        if (cancelled) return;
        setError(caught instanceof ApiClientError ? caught.message : 'Could not load users.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (user?.role !== 'admin') {
    return (
      <section className="card" data-testid="users-page">
        <h2>Users</h2>
        <p className="status status--error">Only an admin can manage users and roles.</p>
      </section>
    );
  }

  async function onCreate(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setNotice(null);
    try {
      const body = await apiRequest<UserResponse>('/api/users', {
        method: 'POST',
        body: { email: draft.email, name: draft.name, role: draft.role },
      });
      setUsers((current) =>
        [...current.filter((entry) => entry.id !== body.user.id), body.user].sort((a, b) =>
          a.email.localeCompare(b.email),
        ),
      );
      setDraft(EMPTY);
      setNotice(`Added ${body.user.email}.`);
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not create the user.');
    }
  }

  async function refreshTokens(userId: string): Promise<void> {
    const listed = await apiRequest<ApiTokenListResponse>(`/api/users/${userId}/tokens`);
    setTokensByUser((current) => ({ ...current, [userId]: listed.tokens }));
  }

  async function onMintToken(target: PublicUser): Promise<void> {
    setError(null);
    setNotice(null);
    setTokenBusy(target.id);
    try {
      const body = await apiRequest<ApiTokenCreateResponse>(`/api/users/${target.id}/tokens`, {
        method: 'POST',
        body: {},
      });
      await refreshTokens(target.id);
      setRawByUser((current) => ({ ...current, [target.id]: body.raw }));
      setNotice(
        `Minted a machine token for ${target.email}. Copy it now — it will not be shown again.`,
      );
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not mint a token.');
    } finally {
      setTokenBusy(null);
    }
  }

  async function onRevokeToken(target: PublicUser, token: PublicApiToken): Promise<void> {
    setError(null);
    setNotice(null);
    setTokenBusy(token.id);
    try {
      await apiRequest<ApiTokenResponse>(`/api/users/${target.id}/tokens/${token.id}/revoke`, {
        method: 'POST',
        body: {},
      });
      await refreshTokens(target.id);
      setNotice(`Revoked the token starting ${token.tokenPrefix}.`);
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not revoke that token.');
    } finally {
      setTokenBusy(null);
    }
  }

  async function onSave(
    target: PublicUser,
    patch: { role?: UserRole; active?: boolean },
  ): Promise<void> {
    setError(null);
    setNotice(null);
    try {
      const body = await apiRequest<UserResponse>(`/api/users/${target.id}`, {
        method: 'PATCH',
        body: patch,
      });
      setUsers((current) =>
        current.map((entry) => (entry.id === body.user.id ? body.user : entry)),
      );
      setNotice(`Updated ${body.user.email}.`);
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not update the user.');
    }
  }

  return (
    <section data-testid="users-page">
      <div className="card">
        <h2>Users</h2>
        <p className="meta">Provision Google accounts. Unknown emails are denied at sign-in.</p>
        {error ? (
          <p className="status status--error" data-testid="users-error">
            {error}
          </p>
        ) : null}
        {notice ? (
          <p className="status status--ok" data-testid="users-notice">
            {notice}
          </p>
        ) : null}
      </div>

      <form className="card" onSubmit={(event) => void onCreate(event)}>
        <h2>Add user</h2>
        <label className="field">
          Email
          <input
            type="email"
            autoComplete="off"
            required
            value={draft.email}
            onChange={(event) => setDraft((current) => ({ ...current, email: event.target.value }))}
            data-testid="user-email"
          />
        </label>
        <label className="field">
          Name
          <input
            type="text"
            autoComplete="off"
            required
            value={draft.name}
            onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
            data-testid="user-name"
          />
        </label>
        <label className="field">
          Role
          <select
            value={draft.role}
            onChange={(event) =>
              setDraft((current) => ({ ...current, role: event.target.value as UserRole }))
            }
            data-testid="user-role"
          >
            {USER_ROLES.map((role) => (
              <option key={role} value={role}>
                {USER_ROLE_LABELS[role]}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" className="button" data-testid="user-create">
          Create user
        </button>
      </form>

      {loading ? <p className="meta">Loading users…</p> : null}

      {users.map((entry) => (
        <article className="card" key={entry.id} data-testid={`user-row-${entry.id}`}>
          <h2>{entry.name}</h2>
          <p className="meta">{entry.email}</p>
          <label className="field">
            Role
            <select
              value={entry.role}
              onChange={(event) => void onSave(entry, { role: event.target.value as UserRole })}
              data-testid={`user-role-${entry.id}`}
            >
              {USER_ROLES.map((role) => (
                <option key={role} value={role}>
                  {USER_ROLE_LABELS[role]}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="button button--ghost"
            onClick={() => void onSave(entry, { active: !entry.active })}
            data-testid={`user-active-${entry.id}`}
          >
            {entry.active ? 'Deactivate' : 'Reactivate'}
          </button>

          <div className="machine-tokens" data-testid={`user-tokens-${entry.id}`}>
            <h3>Machine token (MCP)</h3>
            <p className="meta">
              Grok Bot uses a Bearer token at /mcp. The raw secret is shown once. List shows prefix,
              last used, and revoked — never the secret.
            </p>
            {rawByUser[entry.id] ? (
              <p className="status status--ok" data-testid={`user-token-raw-${entry.id}`}>
                Copy this token now. It will not be shown again: {rawByUser[entry.id]}
              </p>
            ) : null}
            {roleCanHoldMachineToken(entry.role) && entry.active ? (
              <button
                type="button"
                className="button"
                disabled={tokenBusy === entry.id}
                onClick={() => void onMintToken(entry)}
                data-testid={`user-token-mint-${entry.id}`}
              >
                {tokensByUser[entry.id]?.some((row) => !row.revokedAt)
                  ? 'Rotate token'
                  : 'Mint token'}
              </button>
            ) : (
              <p className="meta">
                Tokens can only be minted for an active admin, equipment admin, or staff user.
              </p>
            )}
            <ul className="link-list" data-testid={`user-token-list-${entry.id}`}>
              {(tokensByUser[entry.id] ?? []).map((token) => (
                <li key={token.id} data-testid={`user-token-${token.id}`}>
                  <p>
                    {token.tokenPrefix}…{token.revokedAt ? ' · revoked' : ''}
                  </p>
                  <p className="meta">
                    Last used {token.lastUsedAt ?? 'never'}
                    {token.label ? ` · ${token.label}` : ''}
                  </p>
                  {!token.revokedAt ? (
                    <button
                      type="button"
                      className="button button--ghost"
                      disabled={tokenBusy === token.id}
                      onClick={() => void onRevokeToken(entry, token)}
                      data-testid={`user-token-revoke-${token.id}`}
                    >
                      Revoke
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        </article>
      ))}
    </section>
  );
}
